/** Cliente de la API (functions/products_api). Mismo contrato que usa tests/aws/client.py. */
import { useAuthStore } from "@/stores/auth";

import {
  type AgentHandoffSettings,
  type AgentSettingsResponse,
  type Category,
  type ConnectionsResponse,
  type ConversationResponse,
  type ConversationsResponse,
  type Destination,
  type LabOutputKind,
  type LabSummaryRow,
  type LabTestCreate,
  type LabTestResponse,
  type LabTestSummary,
  type MetaConnection,
  photoContentType,
  type ProductListItem,
  type ProductResponse,
  type PromptDetailResponse,
  type PromptKey,
  type PromptScope,
  type PromptsIndexResponse,
  type PromptText,
  type ReviewPayload,
  type UploadPayload,
  type WhatsAppConnection,
  type WhatsAppConnectPayload,
  type WhatsAppConversation,
  type WhatsAppMessage,
} from "./contracts";

export interface CategoryInfo {
  id: Category;
  label: string;
  description: string;
  size_required: boolean;
  size_label: string;
}

const promptPath = (scope: PromptScope, category: Category, key: PromptKey) =>
  `/prompts/${scope}/${encodeURIComponent(category)}/${encodeURIComponent(key)}`;
const labPath = (id: string) => `/lab/tests/${encodeURIComponent(id)}`;

const BASE = (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** Sin red (fetch rechazado): la PWA lo muestra como "sin conexión", no como error. */
  get offline(): boolean {
    return this.status === 0;
  }
}

const OFFLINE_MESSAGE = "Sin conexión. Revisa tu internet e inténtalo de nuevo.";

async function call<T>(method: string, path: string, body?: unknown, idempotencyKey?: string): Promise<T> {
  const auth = useAuthStore();
  const token = await auth.getIdToken();
  if (!token) {
    auth.signOut();
    throw new ApiError(401, "Tu sesión expiró. Vuelve a entrar.");
  }
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
        ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError(0, OFFLINE_MESSAGE);
  }
  const text = await res.text();
  let data: Record<string, unknown> = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    /* cuerpo no JSON (p. ej. 502 de API Gateway) */
  }
  if (res.status === 401) {
    auth.signOut();
    throw new ApiError(401, "Tu sesión expiró. Vuelve a entrar.");
  }
  if (!res.ok) throw new ApiError(res.status, String(data.error ?? data.message ?? `HTTP ${res.status}`));
  return data as T;
}

export const api = {
  listProducts: () => call<{ products: ProductListItem[] }>("GET", "/products"),
  getProduct: (id: string) => call<ProductResponse>("GET", `/products/${encodeURIComponent(id)}`),
  createProduct: (payload: UploadPayload, idempotencyKey?: string) =>
    call<{ product_id: string }>("POST", "/products", payload, idempotencyKey),
  review: (id: string, payload: ReviewPayload) =>
    call<{ accepted: boolean }>("POST", `/products/${encodeURIComponent(id)}/review`, payload),
  retry: (id: string) => call<{ execution_arn: string }>("POST", `/products/${encodeURIComponent(id)}/retry`),
  deleteProduct: (id: string) => call<Record<string, never>>("DELETE", `/products/${encodeURIComponent(id)}`),

  getConnections: () => call<ConnectionsResponse>("GET", "/connections"),
  /** URL del diálogo de Facebook Login for Business; Meta vuelve a `returnTo?meta=connected|select|error`. */
  startMetaLogin: (returnTo: string) =>
    call<{ url: string }>("POST", "/connections/meta/start", { return_to: returnTo }),
  selectMetaPage: (pageId: string) =>
    call<{ meta: MetaConnection }>("POST", "/connections/meta/select", { page_id: pageId }),
  disconnectMeta: () => call<{ meta: MetaConnection }>("DELETE", "/connections/meta"),
  /** Canjea el code de Embedded Signup (vence a los 30 s: llamar apenas lo entrega Meta). */
  connectWhatsApp: (payload: WhatsAppConnectPayload) =>
    call<{ whatsapp: WhatsAppConnection }>("POST", "/connections/whatsapp", payload),
  selectWhatsAppPhone: (phoneNumberId: string) =>
    call<{ whatsapp: WhatsAppConnection }>("POST", "/connections/whatsapp/select", { phone_number_id: phoneNumberId }),
  disconnectWhatsApp: () => call<{ whatsapp: WhatsAppConnection }>("DELETE", "/connections/whatsapp"),
  /** Agent Settings del agente de Meta del número conectado (solo se edita el handoff). */
  getAgentSettings: () => call<AgentSettingsResponse>("GET", "/connections/whatsapp/agent"),
  updateAgentHandoff: (handoff: AgentHandoffSettings) =>
    call<AgentSettingsResponse>("PUT", "/connections/whatsapp/agent", { handoff }),

  listConversations: () => call<ConversationsResponse>("GET", "/conversations"),
  getConversation: (waId: string, before?: string) =>
    call<ConversationResponse>(
      "GET",
      `/conversations/${encodeURIComponent(waId)}${before ? `?before=${encodeURIComponent(before)}` : ""}`,
    ),
  /** Devuelve la conversación al agente de Meta (thread_control release). */
  releaseConversation: (waId: string) =>
    call<{ conversation: WhatsAppConversation }>("POST", `/conversations/${encodeURIComponent(waId)}/release`),
  /** Toma la conversación (el agente deja de responder); 409 si Meta no lo permite. */
  takeConversation: (waId: string) =>
    call<{ conversation: WhatsAppConversation }>("POST", `/conversations/${encodeURIComponent(waId)}/take`),
  /** Texto de la dueña al cliente por la Cloud API; 409 si pasaron 24 h desde el último mensaje del cliente. */
  sendConversationMessage: (waId: string, text: string) =>
    call<{ conversation: WhatsAppConversation; message: WhatsAppMessage }>(
      "POST",
      `/conversations/${encodeURIComponent(waId)}/messages`,
      { text },
    ),
  resolveOrphan: (productId: string, destination: Destination) =>
    call<Record<string, never>>(
      "DELETE",
      `/connections/orphans/${encodeURIComponent(productId)}/${encodeURIComponent(destination)}`,
    ),

  listCategories: () => call<{ categories: CategoryInfo[] }>("GET", "/categories"),
  listPrompts: () => call<PromptsIndexResponse>("GET", "/prompts"),
  getPrompt: (scope: PromptScope, category: Category, key: PromptKey) =>
    call<PromptDetailResponse>("GET", promptPath(scope, category, key)),
  getPromptVersion: (scope: PromptScope, category: Category, key: PromptKey, version: number) =>
    call<{ version: PromptText }>("GET", `${promptPath(scope, category, key)}/versions/${version}`),
  /** `baseVersion`: la vigente que vio el editor (0 = ninguna); 409 si otra persona guardo entretanto. */
  savePrompt: (scope: PromptScope, category: Category, key: PromptKey, text: string, baseVersion: number, note = "") =>
    call<PromptDetailResponse>("POST", promptPath(scope, category, key), { text, base_version: baseVersion, note }),
  restorePrompt: (scope: PromptScope, category: Category, key: PromptKey, version: number, baseVersion: number) =>
    call<PromptDetailResponse>("POST", `${promptPath(scope, category, key)}/restore`, {
      version,
      base_version: baseVersion,
    }),
  deleteTenantPrompt: (category: Category, key: PromptKey) =>
    call<Record<string, never>>("DELETE", promptPath("tenant", category, key)),

  createLabTest: (payload: LabTestCreate, idempotencyKey?: string) =>
    call<{ test_id: string; status: string }>("POST", "/lab/tests", payload, idempotencyKey),
  listLabTests: (category?: Category | "") =>
    call<{ tests: LabTestSummary[] }>(
      "GET",
      `/lab/tests${category ? `?category=${encodeURIComponent(category)}` : ""}`,
    ),
  getLabSummary: () => call<{ rows: LabSummaryRow[] }>("GET", "/lab/summary"),
  getLabTest: (id: string) => call<LabTestResponse>("GET", labPath(id)),
  rateLabTest: (id: string, kind: LabOutputKind, score: number, note: string) =>
    call<LabTestResponse>("POST", `${labPath(id)}/ratings`, { kind, score, note }),
  deleteLabTest: (id: string) => call<Record<string, never>>("DELETE", labPath(id)),

  /** Pide una URL prefirmada y sube el archivo directo a S3; devuelve la referencia para POST /products. */
  async uploadPhoto(file: File, signal?: AbortSignal): Promise<{ s3_key: string; file_name: string }> {
    const contentType = photoContentType(file);
    const info = await call<{ s3_key: string; upload_url: string; file_name: string }>("POST", "/products/uploads", {
      file_name: file.name,
      content_type: contentType,
    });
    let put: Response;
    try {
      // El Content-Type debe ser el mismo con el que se firmo la URL.
      put = await fetch(info.upload_url, {
        method: "PUT",
        headers: { "Content-Type": contentType },
        body: file,
        signal,
      });
    } catch (err) {
      if (signal?.aborted) throw err;
      throw new ApiError(0, OFFLINE_MESSAGE);
    }
    if (!put.ok) throw new ApiError(put.status, `No se pudo subir ${file.name}.`);
    return { s3_key: info.s3_key, file_name: info.file_name };
  },
};
